/**
 * The task page's binding of the shared detail-page surface.
 *
 * Everything structural - the shell, the drag, the rail, the persistence —
 * lives in `shared/surface`. This file is the whole of what tasks add: its
 * config (`./layout`) and one typed hook over the generic one.
 *
 * It binds the SELECTION-FREE hook: the task page has no detail pane, so it
 * neither names a selection kind nor publishes a `select` that would open a
 * pane it never declared. It DOES have a reader, so the reader third of the
 * API is typed - see `TaskSurfaceContext`.
 */

import {
  useSurfaceWithoutSelection,
  UseDetailSurfaceOptions,
  UseSurfaceWithoutSelectionResult,
} from '../../shared/surface/useDetailSurface';
import { TASK_SURFACE, TASK_TABS, TaskPanelId, TaskTab } from './layout';
import { TaskReaderContent } from './TaskSurfaceContext';

export { TASK_SURFACE, TASK_TABS };
export type { TaskPanelId, TaskTab };

export type TaskWorkspace = UseSurfaceWithoutSelectionResult<
  TaskPanelId,
  TaskReaderContent,
  TaskTab
>;

/**
 * The task page's layout, reader and tab state.
 *
 * Takes NO entity id: the surface is global-scoped, so every task shares one
 * arrangement - see the note at the top of `./layout`.
 */
export const useTaskWorkspace = (
  options: Omit<UseDetailSurfaceOptions<TaskTab>, 'entityId'> = {}
): TaskWorkspace =>
  useSurfaceWithoutSelection<TaskPanelId, TaskReaderContent, TaskTab>(
    TASK_SURFACE,
    options
  );
